import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { ReactComponent as DropdownIcon } from "../../assets/images/dropdown.svg";
import * as api from "../../api";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const pad = (n) => String(n).padStart(2, "0");

const TIER_DOT_PALETTE = [
  "#2563eb",
  "#16a34a",
  "#ea580c",
  "#9333ea",
  "#0891b2",
  "#c026d3",
  "#ca8a04",
];

const shortenTierName = (name) =>
  typeof name === "string"
    ? name.replace(/^(Standard|Flexible) Entry:\s*/i, "").trim()
    : "";

/** Normalize API slot availability payloads for calendar dots (strings, enums, booleans, nested objects). */
function normalizeSlotAvailabilityStatus(raw) {
  if (raw === undefined || raw === null || raw === "") return null;
  if (typeof raw === "boolean") return raw ? "available" : "unavailable";
  if (typeof raw === "number") {
    if (!Number.isFinite(raw)) return null;
    return raw > 0 ? "available" : "unavailable";
  }
  if (typeof raw === "object") {
    const nested =
      raw.status ??
      raw.availability ??
      raw.state ??
      raw.code ??
      raw.label;
    if (nested !== undefined && nested !== raw) {
      return normalizeSlotAvailabilityStatus(nested);
    }
    if (typeof raw.available === "boolean") {
      return raw.available ? "available" : "unavailable";
    }
    if (typeof raw.has_slots === "boolean") {
      return raw.has_slots ? "available" : "unavailable";
    }
    if (typeof raw.hasSlots === "boolean") {
      return raw.hasSlots ? "available" : "unavailable";
    }
    if (raw.slots_available === true || raw.slotsAvailable === true) return "available";
    if (raw.slots_available === false || raw.slotsAvailable === false) return "unavailable";
  }
  let s = String(raw).trim().toLowerCase().replace(/-/g, "_").replace(/\s+/g, "_");
  if (s === "true" || s === "1") return "available";
  if (s === "false" || s === "0") return "unavailable";
  if (s === "soldout") return "sold_out";
  return s;
}

/** Calendar dot only for tiers with real slot inventory that day (parent + subs use the same rule—no faded/disabled dots). */
const TIER_DOT_VISIBLE_STATUSES = new Set([
  "available",
  "open",
  "sold_out",
  "partial",
  "limited",
  "has_slots",
  "has_inventory",
  "active",
  "bookable",
]);

const SlotSelector = ({
  eventPk,
  tierPk,
  tier,
  /** When at least two entries are present, merges availability & slots across all tiers. */
  tierSlotGroup = null,
  eventStartDate,
  maxDaysToShow = null,
  selectedSlot = null,
  onSlotSelect = () => {},
  currency = "",
}) => {
  const tierEntries = useMemo(() => {
    if (!Array.isArray(tierSlotGroup) || tierSlotGroup.length < 2) {
      return null;
    }
    return tierSlotGroup.map((entry, idx) => {
      const tk = entry.tier ?? {};
      const color = entry.color ?? TIER_DOT_PALETTE[idx % TIER_DOT_PALETTE.length];
      const label =
        entry.label != null
          ? shortenTierName(entry.label)
          : shortenTierName(tk.name ?? "");
      return {
        pk: entry.pk,
        tier: tk,
        color,
        label: label || shortenTierName(tk.name ?? ""),
        isSubTier: Boolean(entry.isSubTier),
      };
    });
  }, [tierSlotGroup]);

  const multiTier = tierEntries != null;
  const activeTierPk = tierPk ?? tierEntries?.[0]?.pk;
  const activeTierMeta = tier ?? tierEntries?.[0]?.tier;

  const parsePositiveInteger = useCallback((value) => {
    if (value == null || value === "") return null;
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return Math.floor(parsed);
  }, []);

  const mergedMaxDaysToShowLimit = useMemo(() => {
    if (multiTier && tierEntries) {
      let maxVal = null;
      for (const { tier: t } of tierEntries) {
        const v = parsePositiveInteger(t?.maxDaysToShow ?? t?.max_days_to_show ?? null);
        if (v != null) {
          maxVal = maxVal == null ? v : Math.max(maxVal, v);
        }
      }
      return maxVal;
    }
    return parsePositiveInteger(maxDaysToShow);
  }, [multiTier, tierEntries, maxDaysToShow, parsePositiveInteger]);

  const eventStart = useMemo(
    () => (eventStartDate ? new Date(eventStartDate) : null),
    [eventStartDate]
  );

  const eventEnd = useMemo(() => {
    if (multiTier && tierEntries) {
      const withExpiry = tierEntries.filter(({ tier: t }) => t?.expiresAt);
      if (withExpiry.length === 0) {
        return null;
      }
      let latest = null;
      for (const { tier: t } of withExpiry) {
        const d = new Date(t.expiresAt);
        if (Number.isNaN(d.getTime())) continue;
        if (!latest || d > latest) {
          latest = d;
        }
      }
      return latest;
    }
    return activeTierMeta?.expiresAt ? new Date(activeTierMeta.expiresAt) : null;
  }, [multiTier, tierEntries, activeTierMeta]);

  const today = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  }, []);

  const selectableStartDate = useMemo(() => {
    const eventStartDay = eventStart
      ? new Date(eventStart.getFullYear(), eventStart.getMonth(), eventStart.getDate())
      : null;
    if (eventStartDay && eventStartDay >= today) {
      return eventStartDay;
    }
    return new Date(today.getTime());
  }, [eventStart, today]);

  const calendarEndDate = useMemo(() => {
    if (!eventEnd) return null;
    return new Date(
      eventEnd.getFullYear(),
      eventEnd.getMonth(),
      eventEnd.getDate()
    );
  }, [eventEnd]);

  const slotsWindowEndDate = useMemo(() => {
    if (!mergedMaxDaysToShowLimit) return null;
    const d = new Date(selectableStartDate.getTime());
    d.setDate(d.getDate() + mergedMaxDaysToShowLimit - 1);
    return d;
  }, [mergedMaxDaysToShowLimit, selectableStartDate]);

  const minMonthDate = useMemo(
    () =>
      new Date(
        selectableStartDate.getFullYear(),
        selectableStartDate.getMonth(),
        1
      ),
    [selectableStartDate]
  );
  const maxMonthDate = useMemo(
    () =>
      calendarEndDate
        ? new Date(
            calendarEndDate.getFullYear(),
            calendarEndDate.getMonth(),
            1
          )
        : null,
    [calendarEndDate]
  );

  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    if (eventStart && eventStart >= now) {
      return new Date(eventStart.getFullYear(), eventStart.getMonth(), 1);
    }
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotError, setSlotError] = useState("");
  const [availability, setAvailability] = useState({});
  /** dateStr → { tierPk → status string } when multi-tier */
  const [availabilityByTier, setAvailabilityByTier] = useState({});
  const [loadingAvailability, setLoadingAvailability] = useState(false);

  const slotsRef = useRef(null);
  const slotFetchSeqRef = useRef(0);
  const availFetchSeqRef = useRef(0);

  useEffect(() => {
    setCurrentMonth((prev) => {
      const prevMonth = new Date(prev.getFullYear(), prev.getMonth(), 1);
      if (prevMonth < minMonthDate) return new Date(minMonthDate.getTime());
      if (maxMonthDate && prevMonth > maxMonthDate) {
        return new Date(maxMonthDate.getTime());
      }
      return prev;
    });
  }, [minMonthDate, maxMonthDate]);

  useEffect(() => {
    if (!loadingSlots && slots.length > 0 && slotsRef.current) {
      setTimeout(() => {
        slotsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [slots, loadingSlots]);

  useEffect(() => {
    if (!eventPk) return;
    if (multiTier && tierEntries?.length >= 2) {
      const seq = ++availFetchSeqRef.current;
      const monthStr = `${currentMonth.getFullYear()}-${pad(currentMonth.getMonth() + 1)}`;
      setLoadingAvailability(true);
      setAvailability({});
      setAvailabilityByTier({});
      Promise.all(
        tierEntries.map(({ pk }) => api.events.fetchSlotAvailability(eventPk, pk, monthStr))
      )
        .then((responses) => {
          if (seq !== availFetchSeqRef.current) return;
          const matrix = {};
          responses.forEach((res, idx) => {
            const data = res?.data ?? {};
            const pkStr = String(tierEntries[idx].pk);
            Object.entries(data).forEach(([dateStr, status]) => {
              if (!matrix[dateStr]) {
                matrix[dateStr] = {};
              }
              matrix[dateStr][pkStr] = status;
            });
          });
          setAvailabilityByTier(matrix);
        })
        .catch(() => {
          if (seq !== availFetchSeqRef.current) return;
          setAvailabilityByTier({});
        })
        .finally(() => {
          if (seq === availFetchSeqRef.current) setLoadingAvailability(false);
        });
      return;
    }

    if (!activeTierPk) return;

    const seq = ++availFetchSeqRef.current;
    const monthStr = `${currentMonth.getFullYear()}-${pad(currentMonth.getMonth() + 1)}`;
    setLoadingAvailability(true);
    setAvailability({});
    setAvailabilityByTier({});
    api.events
      .fetchSlotAvailability(eventPk, activeTierPk, monthStr)
      .then((res) => {
        if (seq !== availFetchSeqRef.current) return;
        setAvailability(res?.data ?? {});
      })
      .catch(() => {
        if (seq !== availFetchSeqRef.current) return;
        setAvailability({});
      })
      .finally(() => {
        if (seq === availFetchSeqRef.current) setLoadingAvailability(false);
      });
  }, [currentMonth, eventPk, multiTier, tierEntries, activeTierPk]);

  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    const days = [];

    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({ day: daysInPrevMonth - i, current: false, date: null });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      const isBeforeStart = date < selectableStartDate;
      const isAfterEventEnd = calendarEndDate ? date > calendarEndDate : false;
      days.push({
        day: i,
        current: true,
        date,
        isPast: isBeforeStart || isAfterEventEnd,
      });
    }

    const remaining = 7 - (days.length % 7);
    if (remaining < 7) {
      for (let i = 1; i <= remaining; i++) {
        days.push({ day: i, current: false, date: null });
      }
    }

    return days;
  }, [currentMonth, selectableStartDate, calendarEndDate]);

  const fetchSlotsForDate = useCallback(
    async (date) => {
      const seq = ++slotFetchSeqRef.current;
      const dateStr = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
      setLoadingSlots(true);
      setSlotError("");
      setSlots([]);
      try {
        if (multiTier && tierEntries?.length >= 2) {
          const bundles = await Promise.all(
            tierEntries.map(async (entry) => {
              const res = await api.events.fetchSlots(eventPk, entry.pk, dateStr);
              const results = res?.data?.results ?? res?.data ?? [];
              return {
                entry,
                slots: Array.isArray(results) ? results : [],
              };
            })
          );
          if (seq !== slotFetchSeqRef.current) return;
          const merged = [];
          bundles.forEach(({ entry, slots: tierSlots }) => {
            tierSlots.forEach((slot) => {
              merged.push({
                ...slot,
                _bookingTierId: entry.pk,
                _tierDotColor: entry.color,
                _tierShortLabel: entry.label || shortenTierName(entry.tier?.name),
                _tierCurrency:
                  slot.currency ??
                  slot.region?.currency ??
                  entry.tier?.currency ??
                  "",
              });
            });
          });
          merged.sort((a, b) => {
            const rawA =
              a.start_time || a.startTime || a.time || a.date || a.startsAt || "";
            const rawB =
              b.start_time || b.startTime || b.time || b.date || b.startsAt || "";
            const ta = typeof rawA === "string" && rawA.includes("T")
              ? new Date(rawA).getTime()
              : NaN;
            const tb = typeof rawB === "string" && rawB.includes("T")
              ? new Date(rawB).getTime()
              : NaN;
            if (!Number.isNaN(ta) && !Number.isNaN(tb) && ta !== tb) return ta - tb;
            const sa = `${rawA}`.localeCompare(`${rawB}`);
            if (sa !== 0) return sa;
            return String(a._bookingTierId).localeCompare(String(b._bookingTierId));
          });
          setSlots(merged);
          return;
        }

        const response = await api.events.fetchSlots(eventPk, activeTierPk, dateStr);
        if (seq !== slotFetchSeqRef.current) return;
        const results = response?.data?.results ?? response?.data ?? [];
        setSlots(Array.isArray(results) ? results : []);
      } catch {
        if (seq !== slotFetchSeqRef.current) return;
        setSlotError("Failed to load slots. Please try again.");
      } finally {
        if (seq === slotFetchSeqRef.current) {
          setLoadingSlots(false);
        }
      }
    },
    [eventPk, multiTier, tierEntries, activeTierPk]
  );

  const goToPrevMonth = () => {
    setCurrentMonth((prev) => {
      const next = new Date(prev.getFullYear(), prev.getMonth() - 1, 1);
      return next < minMonthDate ? prev : next;
    });
  };

  const goToNextMonth = () => {
    setCurrentMonth((prev) => {
      const next = new Date(prev.getFullYear(), prev.getMonth() + 1, 1);
      if (maxMonthDate && next > maxMonthDate) {
        return prev;
      }
      return next;
    });
  };
  const canGoPrevMonth = currentMonth > minMonthDate;
  const canGoNextMonth = maxMonthDate ? currentMonth < maxMonthDate : true;

  const isSelectedDateOutsideSlotsWindow = useCallback(
    (date) => {
      if (!date || !slotsWindowEndDate) return false;
      const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const end = new Date(
        slotsWindowEndDate.getFullYear(),
        slotsWindowEndDate.getMonth(),
        slotsWindowEndDate.getDate()
      );
      return d.getTime() > end.getTime();
    },
    [slotsWindowEndDate]
  );

  const dateStrFor = useCallback((date) => {
    if (!date) return "";
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }, []);

  const getMergedMultiDayFlags = useCallback(
    (dayObj) => {
      const ds = dateStrFor(dayObj.date);
      if (!ds || !tierEntries) {
        return { isUnavailable: false };
      }
      const perTier = availabilityByTier[ds] || {};
      const normalizedStatuses = tierEntries.map(({ pk }) =>
        normalizeSlotAvailabilityStatus(perTier[String(pk)])
      );
      const allExplicitlyUnavailable =
        normalizedStatuses.length > 0 &&
        normalizedStatuses.every((n) => n === "unavailable");
      return {
        isUnavailable: allExplicitlyUnavailable,
      };
    },
    [availabilityByTier, tierEntries, dateStrFor]
  );

  const handleDateClick = (dayObj, mergedFlags = null) => {
    if (!dayObj.current || dayObj.isPast) return;
    if (multiTier && tierEntries) {
      const flags = mergedFlags ?? getMergedMultiDayFlags(dayObj);
      if (flags.isUnavailable) return;
    } else {
      const avail = availability[dateStrFor(dayObj.date)];
      if (avail === "unavailable") return;
    }
    setSelectedDate(dayObj.date);
    setSlotError("");
    if (isSelectedDateOutsideSlotsWindow(dayObj.date)) {
      slotFetchSeqRef.current += 1;
      setSlots([]);
      setLoadingSlots(false);
      return;
    }
    fetchSlotsForDate(dayObj.date);
  };

  const isSelected = (dayObj) => {
    if (!selectedDate || !dayObj.date) return false;
    return (
      selectedDate.getFullYear() === dayObj.date.getFullYear() &&
      selectedDate.getMonth() === dayObj.date.getMonth() &&
      selectedDate.getDate() === dayObj.date.getDate()
    );
  };

  const isToday = (dayObj) => {
    if (!dayObj.date) return false;
    const n = new Date();
    return (
      dayObj.date.getFullYear() === n.getFullYear() &&
      dayObj.date.getMonth() === n.getMonth() &&
      dayObj.date.getDate() === n.getDate()
    );
  };

  const getDayAvailabilitySingle = useCallback(
    (dayObj) => {
      if (!dayObj.current || dayObj.isPast || !dayObj.date) return null;
      const dateStr = dateStrFor(dayObj.date);
      return availability[dateStr] ?? null;
    },
    [availability, dateStrFor]
  );

  const formatTime = (timeStr) => {
    if (!timeStr) return null;

    if (timeStr.includes("T") || timeStr.includes(" ")) {
      const d = new Date(timeStr);
      if (Number.isNaN(d.getTime())) return null;
      const formatted = d.toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
      const match = formatted.trim().match(/^(.+?)\s*([AP]M)$/i);
      if (match) {
        return {
          main: match[1],
          meridiem: match[2].toUpperCase(),
        };
      }
      return { main: formatted.trim(), meridiem: "" };
    }

    const [h, m] = timeStr.split(":");
    const hour = parseInt(h, 10);
    if (Number.isNaN(hour)) return null;
    const ampm = hour >= 12 ? "PM" : "AM";
    const h12 = hour % 12 || 12;
    return {
      main: `${h12}:${(m || "00").slice(0, 2)}`,
      meridiem: ampm,
    };
  };

  const parseNumber = (value) => {
    if (value == null) return null;
    if (typeof value === "string" && value.trim() === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  return (
    <div className="slot-selector" onClick={(e) => e.stopPropagation()}>
      <div className={"slot-selector__calendar" + (loadingAvailability ? " slot-selector__calendar--loading" : "")}>
        <div className="slot-selector__calendar-header">
          <button
            className="slot-selector__nav-btn"
            onClick={goToPrevMonth}
            type="button"
            disabled={!canGoPrevMonth}
          >
            <DropdownIcon className="slot-selector__nav-icon slot-selector__nav-icon--prev" />
          </button>
          <span className="slot-selector__month-label">
            {MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </span>
          <button
            className="slot-selector__nav-btn"
            onClick={goToNextMonth}
            type="button"
            disabled={!canGoNextMonth}
          >
            <DropdownIcon className="slot-selector__nav-icon slot-selector__nav-icon--next" />
          </button>
        </div>

        {multiTier && tierEntries && (
          <div className="slot-selector__tier-legend" aria-hidden="true">
            {tierEntries.map((entry) => (
              <span key={entry.pk} className="slot-selector__tier-legend-chip">
                <span
                  className="slot-selector__tier-legend-swatch"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="slot-selector__tier-legend-name">{entry.label}</span>
              </span>
            ))}
          </div>
        )}

        <div className="slot-selector__weekdays">
          {DAYS.map((d) => (
            <div key={d} className="slot-selector__weekday">
              {d}
            </div>
          ))}
        </div>

        <div className="slot-selector__days">
          {calendarDays.map((dayObj, idx) => {
            const multiFlags = multiTier && tierEntries ? getMergedMultiDayFlags(dayObj) : null;
            const dayAvailSingle = multiTier ? null : getDayAvailabilitySingle(dayObj);
            const isUnavailable = multiTier
              ? Boolean(multiFlags?.isUnavailable)
              : dayAvailSingle === "unavailable";
            const isSoldOut = multiTier ? false : dayAvailSingle === "sold_out";
            const isAvailableDay = dayObj.current && !dayObj.isPast && !isUnavailable;
            const ds = dateStrFor(dayObj.date);
            const showTierDotsRow =
              multiTier &&
              tierEntries &&
              multiFlags &&
              dayObj.current &&
              !dayObj.isPast &&
              !isUnavailable;

            /** Only tiers with slots — no invisible placeholders (those shifted dots off-center). */
            const visibleTierDotsForDay =
              showTierDotsRow && !loadingAvailability
                ? tierEntries.filter((entry) => {
                    const raw = availabilityByTier[ds]?.[String(entry.pk)];
                    const n = normalizeSlotAvailabilityStatus(raw);
                    return n != null && TIER_DOT_VISIBLE_STATUSES.has(n);
                  })
                : [];

            const showDotsStrip = visibleTierDotsForDay.length > 0;

            return (
              <button
                key={idx}
                type="button"
                className={
                  "slot-selector__day" +
                  (!dayObj.current ? " slot-selector__day--outside" : "") +
                  (dayObj.isPast ? " slot-selector__day--past" : "") +
                  (isAvailableDay ? " slot-selector__day--available" : "") +
                  (isUnavailable ? " slot-selector__day--unavailable" : "") +
                  (!multiTier && isSoldOut ? " slot-selector__day--sold-out" : "") +
                  (isSelected(dayObj) ? " slot-selector__day--selected" : "") +
                  (isToday(dayObj) ? " slot-selector__day--today" : "") +
                  (multiTier ? " slot-selector__day--multi-tier" : "")
                }
                onClick={() => handleDateClick(dayObj, multiFlags)}
                disabled={!dayObj.current || dayObj.isPast || isUnavailable}
              >
                {dayObj.day}
                {showDotsStrip ? (
                  <span className="slot-selector__day-tier-dots">
                    {visibleTierDotsForDay.map((entry) => {
                      const raw = availabilityByTier[ds]?.[String(entry.pk)];
                      const normalized = normalizeSlotAvailabilityStatus(raw);
                      const dimmedSold = normalized === "sold_out";
                      return (
                        <span key={entry.pk} className="slot-selector__day-tier-slot">
                          <span
                            className="slot-selector__day-tier-dot"
                            title={entry.label || undefined}
                            style={{
                              backgroundColor: entry.color,
                              opacity: dimmedSold ? 0.55 : 1,
                            }}
                          />
                        </span>
                      );
                    })}
                  </span>
                ) : null}
                {isToday(dayObj) && !isSelected(dayObj) && !multiTier ? (
                  <span className="slot-selector__day-dot slot-selector__day-dot--today"></span>
                ) : null}
              </button>
            );
          })}
        </div>

        <div className="slot-selector__legend">
          <span className="slot-selector__legend-item slot-selector__legend-item--sold-out">
            <span className="slot-selector__legend-dot"></span>
            Sold Out
          </span>
          <span className="slot-selector__legend-item slot-selector__legend-item--unavailable">
            <span className="slot-selector__legend-dot"></span>
            Unavailable
          </span>
          {multiTier ? (
            <span className="slot-selector__legend-item slot-selector__legend-item--tier-hint">
              Dots indicate availability per tier
            </span>
          ) : null}
        </div>
      </div>

      {selectedDate && (
        <div className="slot-selector__slots" ref={slotsRef}>
          <p className="slot-selector__slots-title">
            Available Entry Slots &mdash;{" "}
            {selectedDate.toLocaleDateString(undefined, {
              weekday: "short",
              month: "short",
              day: "numeric",
            })}
          </p>

          {isSelectedDateOutsideSlotsWindow(selectedDate) ? (
            <p className="slot-selector__slots-soon">Slots will be available soon</p>
          ) : (
            <>
              {loadingSlots && (
                <div className="slot-selector__loading">
                  <div className="slot-selector__spinner"></div>
                  <span>Loading slots&hellip;</span>
                </div>
              )}

              {slotError && <p className="slot-selector__error">{slotError}</p>}

              {!loadingSlots && !slotError && slots.length === 0 && (
                <p className="slot-selector__no-slots">No slots available for this date.</p>
              )}

              {!loadingSlots && !slotError && slots.length > 0 && (
                <div className="slot-selector__slots-grid">
                  {slots.map((slot) => {
                    const slotKey = slot.id ?? slot.pk;
                    const availableCapacity = parseNumber(
                      slot.availableCapacity ?? slot.available_capacity
                    );
                    const totalCapacity = parseNumber(
                      slot.capacity ?? slot.totalCapacity ?? slot.total_capacity
                    );
                    const isSoldOutFlag = Boolean(
                      slot.isFull ?? slot.is_full ?? slot.soldOut ?? slot.sold_out
                    );
                    const isFull =
                      isSoldOutFlag ||
                      (availableCapacity != null && availableCapacity <= 0) ||
                      (totalCapacity != null && totalCapacity <= 0);
                    const selId = selectedSlot?.id ?? selectedSlot?.pk;
                    const selBt = selectedSlot?._bookingTierId;
                    const curBt = slot?._bookingTierId;
                    const isActive =
                      selId === slotKey &&
                      (selBt == null && curBt == null
                        ? true
                        : String(selBt) === String(curBt));
                    const startTime = formatTime(
                      slot.start_time || slot.startTime || slot.time || slot.date
                    );
                    const endTime =
                      slot.end_time || slot.endTime
                        ? formatTime(slot.end_time || slot.endTime)
                        : null;
                    const slotCurrency =
                      slot._tierCurrency && String(slot._tierCurrency).trim() !== ""
                        ? slot._tierCurrency
                        : currency;

                    return (
                      <button
                        key={`${slotKey}-${slot._bookingTierId ?? "t"}`}
                        type="button"
                        className={
                          "slot-selector__slot-card" +
                          (isActive ? " slot-selector__slot-card--selected" : "") +
                          (isFull ? " slot-selector__slot-card--full" : "") +
                          (slot._tierDotColor ? " slot-selector__slot-card--tagged" : "")
                        }
                        onClick={() => {
                          if (isFull) return;
                          onSlotSelect(slot);
                        }}
                        disabled={isFull}
                      >
                        <span className="slot-selector__slot-card-glow"></span>
                        {slot._tierDotColor ? (
                          <span className="slot-selector__slot-tier-marker">
                            <span
                              className="slot-selector__slot-tier-swatch"
                              style={{ backgroundColor: slot._tierDotColor }}
                            />
                            <span className="slot-selector__slot-tier-name">
                              {slot._tierShortLabel}
                            </span>
                          </span>
                        ) : null}

                        <span className="slot-selector__slot-time-block">
                          <span className="slot-selector__slot-time">
                            <span className="slot-selector__slot-time-main">
                              {startTime?.main || ""}
                            </span>
                            {startTime?.meridiem && (
                              <span className="slot-selector__slot-time-meridiem">
                                {startTime.meridiem}
                              </span>
                            )}
                          </span>
                          {endTime?.main && (
                            <span className="slot-selector__slot-end-time">
                              <span className="slot-selector__slot-time-separator">&ndash;</span>
                              <span className="slot-selector__slot-end-time-main">
                                {endTime.main}
                              </span>
                              {endTime?.meridiem && (
                                <span className="slot-selector__slot-time-meridiem">
                                  {endTime.meridiem}
                                </span>
                              )}
                            </span>
                          )}
                        </span>

                        {slot.price != null && !isFull && (
                          <span className="slot-selector__slot-price">
                            {(slot.price / 100).toLocaleString()} {slotCurrency}
                          </span>
                        )}

                        {availableCapacity != null && (
                          <span className="slot-selector__slot-spots">
                            <span
                              className="slot-selector__slot-spots-bar"
                              style={{
                                width: `${Math.min(
                                  100,
                                  (availableCapacity /
                                    (totalCapacity || availableCapacity || 1)) *
                                    100
                                )}%`,
                              }}
                            ></span>
                            <span className="slot-selector__slot-spots-text">
                              {isFull
                                ? "Full"
                                : `${availableCapacity} spot${availableCapacity !== 1 ? "s" : ""} left`}
                            </span>
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SlotSelector;
